import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showErrorAlert } from "../../utils/showErrorAlert";
import { showSuccessToast } from "../../utils/showToastAlert";

export function useDeleteDistribution({ callApiWithWorker, t }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["deleteDistribution"],

    mutationFn: async ({ id }) => {
      if (!id) throw new Error("Missing distribution id");

      let res;
      try {
        res = await callApiWithWorker(`/distributions/${encodeURIComponent(id)}`, {
          method: "DELETE",
        });
      } catch (err) {
        const status =
          err?.statusCode ??
          err?.status ??
          err?.response?.status ??
          err?.error?.status;

        const message =
          err?.message ??
          err?.error?.message ??
          err?.error?.error ??
          err?.data?.message ??
          err?.response?.data?.message ??
          `Delete failed (${status ?? "unknown"})`;

        const e = new Error(String(message));
        e.statusCode = status;
        e.response = err?.response ?? err;
        throw e;
      }

      const status =
        res?.statusCode ??
        res?.data?.statusCode ??
        res?.status ??
        res?.data?.status;

      if (typeof res?.ok === "boolean" && !res.ok) {
        const e = new Error(`Delete failed (${status ?? "unknown"})`);
        e.statusCode = status;
        e.response = res;
        throw e;
      }

      if (status && status >= 400) {
        const message =
          res?.message ??
          res?.data?.message ??
          res?.error?.message ??
          res?.data?.error?.message ??
          `Delete failed (${status})`;
        const e = new Error(String(message));
        e.statusCode = status;
        e.response = res;
        throw e;
      }

      return res;
    },

    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["distributions"] });
      queryClient.invalidateQueries({ queryKey: ["distributionsByIds"] });

      if (vars?.id) {
        queryClient.invalidateQueries({ queryKey: ["distribution", vars.id] });
      }

      showSuccessToast(t?.("common.deleteSuccessMessage") || "Deleted successfully", {
        title: t?.("common.deleteSuccessTitle") || "Deleted!",
      });
    },

    onError: (err) => {
      showErrorAlert(err, t);
    },
  });
}
